import { extractLocationDetails } from 'app/App';
import { ContactSupportRequestState } from 'communication/types/ContactSupportRequest';
import { Logger, PubSub, PubSubCallback } from '@instinet-analytics/core-lib';
import { Store } from 'redux';
import State from 'store/State';
import ContactSupportService from 'app/contactSupport/ContactSupportService';

export const MessagePopupStateRequest = 'SupportVis.MessagePopupStateRequest';
export const MessagePopupStateResponse = 'SupportVis.MessagePopupStateResponse';

abstract class PopupWindowManagerBase {
    private _dispose: () => void;
    protected logger: Logger;

    protected constructor(options: { logger: Logger }) {
        this.logger = options.logger;

        const handler = this.handleEvent.bind(this);
        window.addEventListener('message', handler);
        this._dispose = () => window.removeEventListener('message', handler);
    }

    protected abstract handleMessage(type: string, payload?: unknown): void;

    private handleEvent(
        e: MessageEvent<string | { source: string } | { type: string }>
    ) {
        if (e.origin !== window.location.origin) {
            return;
        }

        const dataWithSource = e.data as { source: string };
        if (
            dataWithSource.source &&
            dataWithSource.source.toString().startsWith('react-devtools')
        ) {
            return;
        }

        const dataWithType = e.data as { type: string };
        if (
            dataWithType.type &&
            dataWithType.type.toString().startsWith('webpack')
        ) {
            return;
        }

        try {
            if (typeof e.data !== 'string') {
                this.logger.debug('Data is not a string', e.data);
                throw new Error('Data is not a string');
            }

            const received = JSON.parse(e.data);

            if (!received.type) {
                this.logger.debug(
                    'Expected "type" property not found',
                    received
                );
                throw new Error('Expected "type" property not found');
            }

            this.handleMessage(received.type, received.payload);
        } catch (err) {
            this.logger.debug('Ignoring message', { data: e.data, err });
        }
    }

    public dispose() {
        this._dispose();
    }
}

export class PopupParentManager extends PopupWindowManagerBase {
    private popup: Window | null = null;
    public store?: Store<State>;
    public contactSupportService: ContactSupportService;

    public constructor(options: {
        logger: Logger;
        contactSupportService: ContactSupportService;
    }) {
        super(options);
        this.logger = options.logger;
        this.contactSupportService = options.contactSupportService;
        this.logger.debug('Created PopupParentManager');
    }

    public openPopup() {
        const sanitisePathName = (p: string) => {
            if (p.endsWith('.html')) {
                const index = p.lastIndexOf('/');
                return p.substring(0, index);
            }
            return p;
        };
        const path =
            window.location.origin +
            sanitisePathName(window.location.pathname) +
            '#_supportVisPopup_';

        this.popup = window.open(path, 'SupportVisPopup', 'popup=1');
    }

    protected override handleMessage(type: string, _payload?: unknown) {
        if (!this.store) {
            throw new Error('Store has not been set');
        }
        if (this.popup && type === MessagePopupStateRequest) {
            this.popup.postMessage(
                JSON.stringify({
                    type: MessagePopupStateResponse,
                    payload: this.contactSupportService.getSupportState(),
                })
            );
        }
    }
}

export class PopupChildManager extends PopupWindowManagerBase {
    private notify: (msg: string) => void;
    private pubSub: PubSub<ContactSupportRequestState>;
    private requested = false;

    public constructor(options: {
        logger: Logger;
        notify: (msg: string) => void;
    }) {
        super(options);
        this.logger = options.logger;
        this.logger.debug('Created PopupChildManager');
        this.notify = options.notify;
        this.pubSub = new PubSub<ContactSupportRequestState>();
    }

    public requestParentState() {
        if (!window.opener) {
            this.notify(
                'Parent window not found - re-open the popup to use this feature'
            );
        }
        this.logger.debug('Requesting parent state');
        window.opener.postMessage(
            JSON.stringify({ type: MessagePopupStateRequest })
        );
    }

    public subscribeReceivedState(
        id: string,
        cb: PubSubCallback<ContactSupportRequestState>
    ) {
        this.pubSub.subscribe(id, cb);
    }

    public unsubscribeReceivedState(id: string) {
        this.pubSub.unsubscribe(id);
    }

    protected override handleMessage(type: string, payload?: unknown) {
        if (type === MessagePopupStateResponse) {
            this.logger.debug('Received state from parent', payload);
            this.pubSub.publish(payload as ContactSupportRequestState);
        }
    }

    public firstTimeStateRequest() {
        if (this.requested) {
            return;
        }
        this.requested = true;
        this.requestParentState();
    }
}

export function isSupportVisPopup() {
    const { layout } = extractLocationDetails(window.location);
    return layout === '_supportVisPopup_';
}
